// Stimulation (Fracturing) Timeline — constants (lanes, codes, colors).
//
// CODES: DRAFT starter set (plug-and-perf, slickwater-style). The authoritative
// codes come from a JSON template bound at well creation (mirrors the drilling
// `default-iadc-v1` model, EPIC §6 / docs/EPIC_STIMULATION_TIMELINE.md). Phase 0
// ships this hard-coded as the `frac-default-v1` snapshot stamped on every entry.
// SME-confirm before treating these as authoritative completion codes.

import type { Lane } from "./types";

// Generic grid/lane geometry lives in the shared engine; re-export the bits the
// stimulation screen needs so its `./constants` import sites stay engine-agnostic.
export {
  MS_PER_HOUR,
  MS_PER_DAY,
  WINDOW_PAD_HOURS,
  WINDOW_HOURS,
  WINDOW_MS,
  SNAP_MINUTES,
  DEFAULT_BLOCK_HOURS,
  LANE_LABEL_WIDTH,
} from "@geothermal/timeline-core";

// Code library snapshot stamped on every stimulation entry (selected at well
// creation alongside the cost library — same model as the drilling timeline).
export const CODING_TEMPLATE_ID = "frac-default-v1";
export const CODING_TEMPLATE_VERSION = "1";

// ── Default stimulation lanes ───────────────────────────────────────────────
// FRAC is the value-add spine → it is the CENTERED critical path. Perforating +
// pump-down (the wireline conveyance that brings each stage's perfs/plug to
// depth) stack ABOVE; plug-drillout, coiled tubing, flowback, equipment, and HSE
// stack BELOW. Weather pinned to the top (mirrors the drilling roster).
export const FRAC_LANE_ID = "lane-frac";
export const PERF_LANE_ID = "lane-perf";
export const WEATHER_LANE_ID = "lane-weather";
// Two-band relayout (feat/stimulation-two-band-layout): new pad-shared services.
// TCP = Tubing-Conveyed Perforating (a perforating method distinct from wireline
// plug-and-perf); Crane = rig-up/rig-down; Logistics = sand/water/chem supply.
export const TCP_LANE_ID = "lane-tcp";
export const CRANE_LANE_ID = "lane-crane";
export const LOGISTICS_LANE_ID = "lane-logistics";
export const PLUG_LANE_ID = "lane-plug";

// ── SIMOPS-bores critical path (item 3) ─────────────────────────────────────
// In the two-container zipper layout each SELECTED bore is ONE lane, and its
// critical-path OPERATIONS render as color-coded entries WITHIN that lane. Those
// operations are the plug-and-perf stage loop: Perf → Frac → set Plug. (This
// revises the C3 fold that had moved perf/plug onto the shared common band — with
// bores-as-lanes those ops belong to each bore's own critical path; the common
// band keeps the shared EQUIPMENT utilization. Drill-out/flowback are the future
// 3rd (workover) timeline paradigm, not here.)
export const CRITICAL_PATH_LANE_IDS: ReadonlySet<string> = new Set<string>([
  PERF_LANE_ID,
  FRAC_LANE_ID,
  PLUG_LANE_ID,
]);

/** True for an operation that belongs on a bore's SIMOPS critical-path lane. */
export const isCriticalPathLane = (laneId: string): boolean =>
  CRITICAL_PATH_LANE_IDS.has(laneId);

export const DEFAULT_STIMULATION_LANES: readonly Lane[] = [
  {
    id: WEATHER_LANE_ID,
    label: "Weather",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 3,
  },
  {
    id: PERF_LANE_ID,
    label: "Perforating",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 2,
  },
  {
    id: "lane-pumpdown",
    label: "Pump-down / Wireline",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 1,
  },
  {
    id: FRAC_LANE_ID,
    label: "Frac",
    type: "workstream",
    isCriticalPath: true,
    position: "critical",
    order: 0,
  },
  {
    id: "lane-plug",
    label: "Plug / Drill-out",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 0,
  },
  {
    id: "lane-coil",
    label: "Coiled Tubing",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 1,
  },
  {
    id: "lane-flowback",
    label: "Flowback / Cleanup",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 2,
  },
  {
    id: "lane-equipment",
    label: "Frac Spread",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 3,
  },
  {
    id: "lane-hse",
    label: "HSE",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 4,
  },
] as const;

// Lane OBJECTS for the new pad-shared services (TCP / Crane / Logistics) are
// intentionally NOT added to DEFAULT_STIMULATION_LANES: that array is the
// PER-WELL render roster, and injecting shared services there would repeat them
// on every well (the anti-pattern this relayout removes) and displace Weather
// from the top. Their Lane objects are assembled into the COMMON band's roster
// in the layout phase; here we only register their band membership + codes/colors
// so the classification and picklists are ready.

/** The standard roster — these lanes are NOT user-deletable. */
export const DEFAULT_LANE_IDS: ReadonlySet<string> = new Set(
  DEFAULT_STIMULATION_LANES.map((l) => l.id),
);

/** True for a built-in lane; false for a user-added one (which can be deleted). */
export function isDefaultLane(laneId: string): boolean {
  return DEFAULT_LANE_IDS.has(laneId);
}

// ── Band classification (two-band relayout) ─────────────────────────────────
// The Stimulation timeline splits into a pad-shared COMMON band (weather + the
// shared services — one spread, one wireline crew, etc. serve the whole pad) and
// a per-WELL band that shows only each well's frac critical path. The layout
// (Phase C) consumes this to render the two bands, and the Frac-spread
// aggregation (Phase B) projects the per-well frac lanes up into the common band.
export type LaneBand = "common" | "well";

/** Explicit pad-shared (common-band) membership — the single source of truth for
 *  band classification, DECOUPLED from the per-well render roster
 *  (DEFAULT_STIMULATION_LANES). A lane is 'common' by being in THIS set, not by
 *  being on the roster, so a shared service can live on the common band without
 *  being repeated as a row on every well.
 *  NOTE (C3 done): HSE joined the common band and Flowback is PARKED (post-frac
 *  phase). The legacy perf/plug/equipment lanes are FOLDED — their operation
 *  codes moved onto Wireline/Coiled-tubing/Crane — so they stay classified common
 *  but render on NEITHER band (effectively parked). Any pre-existing entries on
 *  those lanes would be orphaned — a demo-seed cosmetic, tracked as follow-up. */
export const COMMON_LANE_IDS: ReadonlySet<string> = new Set<string>([
  WEATHER_LANE_ID,
  PERF_LANE_ID,
  "lane-pumpdown",
  "lane-plug",
  "lane-coil",
  "lane-flowback",
  "lane-equipment",
  "lane-hse",
  TCP_LANE_ID,
  CRANE_LANE_ID,
  LOGISTICS_LANE_ID,
]);

/** Which band a lane renders in. The per-well band is the frac critical path
 *  plus any user-added lane (lanes are added per-well today); every registered
 *  shared service sits on the pad-shared common band. */
export function laneBand(laneId: string): LaneBand {
  return COMMON_LANE_IDS.has(laneId) ? "common" : "well";
}

export const isCommonLane = (laneId: string): boolean =>
  COMMON_LANE_IDS.has(laneId);
export const isWellLane = (laneId: string): boolean =>
  !COMMON_LANE_IDS.has(laneId);

// The EDITABLE pad-shared lanes on the common band = the locked-7 (minus the
// DERIVED Frac-spread lane, which the band renders read-only from the per-well
// aggregation) PLUS HSE (PO kept it, C3). Flowback is PARKED (post-frac phase).
// The legacy operation codes (perf, set-plug, drill-out, spread rig-up/down/
// maint) are FOLDED into the surviving lanes' code lists below (C3) so those ops
// stay loggable without their own lanes.
export const COMMON_BAND_LANES: readonly Lane[] = [
  {
    id: WEATHER_LANE_ID,
    label: "Weather",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 2,
  },
  {
    id: "lane-pumpdown",
    label: "Wireline + Pump-down",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 1,
  },
  {
    id: TCP_LANE_ID,
    label: "TCP",
    type: "workstream",
    isCriticalPath: false,
    position: "above",
    order: 0,
  },
  {
    id: "lane-coil",
    label: "Coiled Tubing",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 0,
  },
  {
    id: CRANE_LANE_ID,
    label: "Crane / Rig-up",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 1,
  },
  {
    id: LOGISTICS_LANE_ID,
    label: "Logistics",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 2,
  },
  {
    id: "lane-hse",
    label: "HSE",
    type: "workstream",
    isCriticalPath: false,
    position: "below",
    order: 3,
  },
] as const;

/** Pad-shared-services entries persist per PAD (not per job) — a pad has one
 *  shared spread/wireline/etc. crew. Keyed by padId. */
export const STIMULATION_PAD_STORAGE_PREFIX = "oxbo-stimulation-pad-v1:";

// ── Codes ───────────────────────────────────────────────────────────────────
export interface CodeOption {
  value: string;
  label: string;
}

/** Operation codes by lane (DRAFT). code1 of an entry; the Phase 1 entry modal
 *  will surface these as the lane's task picklist. Kept lean for Phase 0. */
export const STIMULATION_CODES: Record<string, readonly CodeOption[]> = {
  [FRAC_LANE_ID]: [
    { value: "BREAKDOWN", label: "Breakdown / Step-down" },
    { value: "PAD", label: "Pad" },
    { value: "PROPPANT", label: "Proppant Ramp" },
    { value: "FLUSH", label: "Flush / Displacement" },
    { value: "SCREENOUT", label: "Screenout" },
    { value: "NPT", label: "NPT" },
    { value: "WAIT", label: "Wait" },
  ],
  [PERF_LANE_ID]: [
    { value: "RIH_GUNS", label: "RIH Guns" },
    { value: "CORRELATE", label: "Correlate / Depth-control" },
    { value: "PERF_RUN", label: "Perforate" },
    { value: "POOH_GUNS", label: "POOH Guns" },
    { value: "MISRUN", label: "Misrun / Misfire" },
  ],
  "lane-pumpdown": [
    { value: "RU_WIRELINE", label: "Rig-up Wireline" },
    { value: "PUMP_DOWN", label: "Pump-down" },
    { value: "RD_WIRELINE", label: "Rig-down Wireline" },
    // Folded from the legacy Perforating lane — wireline plug-and-perf (C3):
    { value: "RIH_GUNS", label: "RIH Guns" },
    { value: "CORRELATE", label: "Correlate / Depth-control" },
    { value: "PERF_RUN", label: "Perforate" },
    { value: "POOH_GUNS", label: "POOH Guns" },
    { value: "MISRUN", label: "Misrun / Misfire" },
    { value: "SET_PLUG", label: "Set Plug" }, // folded from the legacy Plug lane
  ],
  "lane-plug": [
    { value: "SET_PLUG", label: "Set Plug" },
    { value: "DRILL_OUT", label: "Drill-out Plugs" },
  ],
  "lane-coil": [
    { value: "CT_RU", label: "CT Rig-up" },
    { value: "CT_RIH", label: "CT RIH" },
    { value: "CT_CLEANOUT", label: "CT Cleanout" },
    { value: "CT_POOH", label: "CT POOH" },
    { value: "CT_RD", label: "CT Rig-down" },
    { value: "DRILL_OUT", label: "Drill-out Plugs" }, // folded from the legacy Plug lane (C3)
  ],
  "lane-flowback": [
    { value: "FLOWBACK", label: "Flowback" },
    { value: "CLEANUP", label: "Cleanup" },
  ],
  "lane-equipment": [
    { value: "SPREAD_RU", label: "Spread Rig-up" },
    { value: "RELOAD_SAND", label: "Reload Sand" },
    { value: "MAINT", label: "Maintenance" },
    { value: "SPREAD_RD", label: "Spread Rig-down" },
  ],
  "lane-hse": [
    { value: "SAFETY_MEETING", label: "Safety Meeting" },
    { value: "INCIDENT", label: "Incident" },
  ],
  [TCP_LANE_ID]: [
    { value: "RIH_TCP", label: "RIH TCP Guns" },
    { value: "CORRELATE_TCP", label: "Correlate / Depth-control" },
    { value: "FIRE_TCP", label: "Fire / Perforate" },
    { value: "POOH_TCP", label: "POOH TCP" },
    { value: "TCP_MISRUN", label: "Misrun / Misfire" },
  ],
  [CRANE_LANE_ID]: [
    { value: "RIG_UP", label: "Rig-up" },
    { value: "RIG_DOWN", label: "Rig-down" },
    { value: "MOVE_EQUIP", label: "Move Equipment" },
    { value: "CRANE_LIFT", label: "Crane Lift" },
    // Folded from the legacy Frac-Spread equipment lane (C3):
    { value: "SPREAD_RU", label: "Spread Rig-up" },
    { value: "SPREAD_RD", label: "Spread Rig-down" },
    { value: "MAINT", label: "Maintenance" },
  ],
  [LOGISTICS_LANE_ID]: [
    { value: "SAND_DELIVERY", label: "Sand Delivery" },
    // Reuse the existing code1 value (also on lane-equipment) so the same
    // real-world operation stays ONE queryable bucket, not two labels.
    { value: "RELOAD_SAND", label: "Reload Sand" },
    { value: "WATER_TRANSFER", label: "Water Transfer" },
    { value: "CHEM_DELIVERY", label: "Chemical Delivery" },
  ],
};

/** Zero-duration milestone codes (cyan I-beam). */
export const STIMULATION_MILESTONES: readonly CodeOption[] = [
  { value: "STAGE_START", label: "Stage Start" },
  { value: "ISIP", label: "ISIP Recorded" },
  { value: "PLUG_SET", label: "Plug Set" },
  { value: "JOB_COMPLETE", label: "Job Complete" },
];

const ALL_CODE_OPTIONS: readonly CodeOption[] = [
  ...Object.values(STIMULATION_CODES).flat(),
  ...STIMULATION_MILESTONES,
];

/** Tasks (main codes) available on a lane. */
export function tasksForLane(laneId: string): readonly CodeOption[] {
  return STIMULATION_CODES[laneId] ?? [];
}

/** Display label for a code across all lane/milestone lists (falls back raw). */
export function labelForCode(code: string): string {
  if (!code) return "";
  return ALL_CODE_OPTIONS.find((o) => o.value === code)?.label ?? code;
}

// ── Reports (item 1, 2026-07-11) ────────────────────────────────────────────
// A Report entry is a zero-duration gray T-beam that LAUNCHES a report screen.
// code1 = report family, code2 = sub-report. Mirrors the drilling timeline's
// report-launch wiring (features/timeline/constants.ts). Reports are NOT
// lane-driven — a report entry can sit in any lane; its family drives the route.
//
// Two tiers (PO 2026-07-11):
//   • REUSED from drilling (screens live TODAY): the HSE families + Flowback /
//     Slurry Disposal → launch the existing drilling report screens.
//   • Phase-3 frac-specific (screens NOT built yet): Frac Stage / Production
//     Interval / Proppant / Stimulation Fluid — creatable as timeline markers
//     now, but launching surfaces a "coming in Phase 3" notice (route is null)
//     until the EPIC Phase-3 screens land (then flip `live` + set `path`).
export const REPORT_TYPES: readonly CodeOption[] = [
  // Reused — real screens, live today.
  { value: "hse-safety", label: "HSE — Safety" },
  { value: "hse-well-control", label: "HSE — Well Control" },
  { value: "hse-problem", label: "HSE — Problem Log" },
  { value: "hse-stimulation-hazards", label: "HSE — Stimulation Hazards" },
  { value: "flowback-disposal", label: "Flowback / Slurry Disposal" },
  // Phase-3 frac-specific — screens pending (EPIC Phase 3).
  { value: "frac-stage", label: "Frac Stage (Phase 3)" },
  { value: "production-interval", label: "Production Interval (Phase 3)" },
  { value: "proppant", label: "Proppant (Phase 3)" },
  { value: "stimulation-fluid", label: "Stimulation Fluid (Phase 3)" },
];

/** Report sub-reports (code2). Only the two route-per-subtype HSE screens carry
 *  them; every other family is a single form (no sub-report). */
export const REPORT_SUB: Record<string, readonly CodeOption[]> = {
  "hse-safety": [
    { value: "observation", label: "Observation" },
    { value: "near-miss", label: "Near Miss" },
    { value: "property-damage", label: "Property Damage" },
    { value: "spill-leak", label: "Spill / Leak" },
    { value: "injury", label: "Injury" },
  ],
  "hse-well-control": [
    { value: "active", label: "Active" },
    { value: "passive", label: "Passive" },
    { value: "kill-sheet", label: "Kill Sheet" },
  ],
};

/** Report family → its report screen route. `subInPath` ⇒ code2 is the
 *  `:subType` route segment. `live: false` ⇒ the screen isn't built yet
 *  (Phase 3) — reportRouteFor returns null and the caller shows a notice. */
interface ReportRoute {
  path: string;
  subInPath: boolean;
  live: boolean;
}
export const REPORT_ROUTE: Record<string, ReportRoute> = {
  // Reused drilling report screens (live).
  "hse-safety": { path: "/safety", subInPath: true, live: true },
  "hse-well-control": { path: "/well-control", subInPath: true, live: true },
  "hse-problem": { path: "/problem", subInPath: false, live: true },
  "hse-stimulation-hazards": {
    path: "/drilling-hazards",
    subInPath: false,
    live: true,
  },
  "flowback-disposal": {
    path: "/solids-disposal",
    subInPath: false,
    live: true,
  },
  // Phase-3 frac-specific — not built yet (path empty, live false).
  "frac-stage": { path: "", subInPath: false, live: false },
  "production-interval": { path: "", subInPath: false, live: false },
  proppant: { path: "", subInPath: false, live: false },
  "stimulation-fluid": { path: "", subInPath: false, live: false },
};

/** Sub-report options for a report family, or null when it's a single form. */
export function reportSubCodesFor(code1: string): readonly CodeOption[] | null {
  const subs = REPORT_SUB[code1];
  return subs && subs.length > 0 ? subs : null;
}

/** True iff the report family's screen exists today (vs a Phase-3 placeholder). */
export function isReportLive(code1: string): boolean {
  return REPORT_ROUTE[code1]?.live ?? false;
}

export interface ReportLaunchContext {
  borefieldId: string;
  boreId: string;
  jobId: string;
  startTs: string; // ISO — the entry's start; the report seeds from ?at=.
}

/** Build the launch URL for a report family (+ sub-report), carrying the
 *  bore/job context and the entry's start time (?at=). Returns null for an
 *  unknown family OR a Phase-3 family whose screen isn't built yet (the caller
 *  surfaces a "coming soon" notice in that case). */
export function reportRouteFor(
  code1: string,
  code2: string,
  ctx: ReportLaunchContext,
): string | null {
  const route = REPORT_ROUTE[code1];
  if (!route || !route.live || !route.path) return null;
  let path = route.path;
  if (route.subInPath) {
    // subType is a required route segment; default to the first sub if unset.
    const sub = code2 || REPORT_SUB[code1]?.[0]?.value || "";
    path = `${route.path}/${sub}`;
  }
  const params = new URLSearchParams({
    borefieldId: ctx.borefieldId,
    boreId: ctx.boreId,
    jobId: ctx.jobId,
    at: ctx.startTs,
  });
  return `${path}?${params.toString()}`;
}

const REPORT_CODE_OPTIONS: readonly CodeOption[] = [
  ...REPORT_TYPES,
  ...Object.values(REPORT_SUB).flat(),
];

/** Report-family (or sub-report) label; falls back to the code library, then
 *  the raw code so the T-beam always renders something. */
export function reportLabel(code1: string): string {
  return (
    REPORT_CODE_OPTIONS.find((o) => o.value === code1)?.label ??
    labelForCode(code1)
  );
}

// ── Value-flow coloring of the CRITICAL (Frac) path ─────────────────────────
// Same value-stream idea as drilling: value-ADDED placement work is GREEN,
// necessary-but-non-value diagnostics are GRAY, screenout / NPT are RED.
export const VALUE_GREEN_DARK = "#15803d"; // proppant placement — the core value
export const VALUE_GREEN = "#16a34a"; // pad / flush
export const WASTE_GRAY = "#6b7280"; // necessary but non-value (breakdown, wait)
export const REMEDIAL_RED = "#dc2626"; // screenout, NPT

export const CRITICAL_VALUE_COLOR: Record<string, string> = {
  PROPPANT: VALUE_GREEN_DARK,
  PAD: VALUE_GREEN,
  FLUSH: VALUE_GREEN,
  BREAKDOWN: WASTE_GRAY,
  WAIT: WASTE_GRAY,
  SCREENOUT: REMEDIAL_RED,
  NPT: REMEDIAL_RED,
};

export function criticalPathColor(code1: string): string {
  return CRITICAL_VALUE_COLOR[code1] ?? WASTE_GRAY;
}

/** Frac-lane codes that count as active placement ("pump time") — the value-add
 *  green set. BREAKDOWN/WAIT (necessary but non-value) and SCREENOUT/NPT
 *  (remedial) are deliberately EXCLUDED, so the per-stage "h pumping" reflects
 *  real treating time, not idle/diagnostic time on the frac line. */
export const PUMP_CODES: ReadonlySet<string> = new Set([
  "PROPPANT",
  "PAD",
  "FLUSH",
]);

export const VALUE_FLOW_LEGEND: readonly { label: string; color: string }[] = [
  { label: "Value-add", color: VALUE_GREEN_DARK },
  { label: "Necessary", color: WASTE_GRAY },
  { label: "Screenout / NPT", color: REMEDIAL_RED },
];

// ── Per-lane bar color for OFF-critical-path lanes ──────────────────────────
export const LANE_COLOR: Record<string, string> = {
  [PERF_LANE_ID]: "#ea580c", // orange — perforating (shaped-charge work)
  "lane-pumpdown": "#0891b2", // cyan — wireline conveyance
  "lane-plug": "#7c3aed", // violet — plugs
  "lane-coil": "#0d9488", // teal — coiled tubing
  "lane-flowback": "#2563eb", // blue — flowback fluids
  "lane-equipment": "#6b7280", // gray — spread
  "lane-hse": "#f59e0b", // amber — HSE
  [TCP_LANE_ID]: "#b45309", // dark amber — tubing-conveyed perforating
  [CRANE_LANE_ID]: "#52525b", // zinc — crane / rig-up
  [LOGISTICS_LANE_ID]: "#0369a1", // deep blue — sand/water/chem logistics
};

export function laneColor(laneId: string): string | undefined {
  return LANE_COLOR[laneId];
}

/** Default fallback color for any code without an explicit mapping. */
export const DEFAULT_CODE_COLOR = "#64748b";

/** Off-critical bar color = the lane's color, else the neutral default. */
export function colorForCode1(_code1: string): string {
  return DEFAULT_CODE_COLOR;
}

/** localStorage key prefix for the demo persistence path (keyed by jobId).
 *  Bump the version when the seed/lane/model shape changes. */
export const STIMULATION_STORAGE_PREFIX = "oxbo-stimulation-v1:";
